import dayjs from "dayjs";
import { motion } from "framer-motion";
import { Box, Flex, Text, Badge, Wrap } from "@chakra-ui/react";
import { CalendarPlus } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { GarbageIcon } from "./GarbageIcon";
import ICalendarLink from "react-icalendar-link";
import { daysLeftTo, getDaysLeftDescription } from "../helpers/helpers";
import { colors, GARBAGES, THEME, gradientColors } from "../helpers/constans";
import { AppTheme } from "../theme";

type GarbageProps = {
  $bgcolor: string;
  $gradient?: string;
  $color?: string;
};

// Modern styled garbage badge with gradient
const Garbage = styled.div<GarbageProps>`
  border-radius: ${THEME.borderRadiusFull};
  color: ${(props) => (props.$color ? props.$color : "white")};
  font-size: 0.72rem;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding: 0.35rem 0.8rem;
  background: ${(props) => props.$gradient || props.$bgcolor};
  box-shadow: 0 2px 10px ${(props) => props.$bgcolor}40;
  display: inline-flex;
  align-items: center;
  margin: 0.2rem;
  transition: all ${THEME.transitionFast};
  letter-spacing: 0.02em;

  &:hover {
    transform: scale(1.05) translateY(-1px);
    box-shadow: 0 4px 15px ${(props) => props.$bgcolor}50;
  }
`;

// Styled calendar button with modern look
const AddToCalendarButton = styled.button<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: ${THEME.borderRadiusFull};
  background: ${({ $theme }) => $theme.gradientPrimary};
  color: white;
  font-weight: 600;
  font-size: 0.72rem;
  font-family: 'Plus Jakarta Sans', sans-serif;
  border: none;
  cursor: pointer;
  box-shadow: 0 2px 10px ${({ $theme }) => $theme.primary}30;
  transition: all ${THEME.transitionBase};
  letter-spacing: 0.02em;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px ${({ $theme }) => $theme.primary}40;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px ${({ $theme }) => $theme.primary}30;
  }
`;

// Animation variants for Framer Motion
const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
  }
};

const generateEvent = (item: any, typesOfGarbage: any) => {
  const startDate = dayjs(item.date).hour(6).minute(0).second(0);
  const endDate = dayjs(item.date).hour(8).minute(0).second(0);
  
  return {
    title: `Odbiór odpadów - ${typesOfGarbage
      .map((garbage: any) => GARBAGES[garbage])
      .join(", ")}`,
    description: typesOfGarbage
      .map((garbage: any) => GARBAGES[garbage])
      .join(", "),
    startTime: startDate.toISOString(),
    endTime: endDate.toISOString(),
    location: "",
  };
};

// Icon container with hover effect
const IconWrapper = styled.div`
  display: inline-block;
  padding: 0.15rem;
  transition: all ${THEME.transitionFast};
  border-radius: ${THEME.borderRadiusSm};

  &:hover {
    transform: scale(1.15) rotate(5deg);
    background: rgba(16, 185, 129, 0.1);
  }
`;

const renderGarbageIcons = (typesOfGarbage: any) =>
  typesOfGarbage.map((garbage: any) => (
    <IconWrapper key={garbage}>
      <GarbageIcon garbage={garbage} />
    </IconWrapper>
  ));

const renderGarbageLabels = (typesOfGarbage: any) =>
  typesOfGarbage.map((garbage: any) => (
    <Garbage
      key={garbage}
      $color={garbage === "PLASTIC" ? "#1F2937" : "white"}
      $bgcolor={colors[garbage]}
      $gradient={gradientColors[garbage]}
    >
      {GARBAGES[garbage]}
    </Garbage>
  ));

// Urgent pulse animation
const UrgentPulse = styled(motion.div)<{ $theme: AppTheme }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle at 70% 30%, ${({ $theme }) => $theme.red}20, transparent 70%);
  border-top-right-radius: ${THEME.borderRadius};
  z-index: 0;
`;

// Helper function to detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Generate and download .ics file for iOS
const downloadICSFile = (event: any) => {
  // Format dates in local time (iOS prefers local timezone)
  const startDate = dayjs(event.startTime).format('YYYYMMDDTHHmmss');
  const endDate = dayjs(event.endTime).format('YYYYMMDDTHHmmss');
  const dtStamp = dayjs().format('YYYYMMDDTHHmmss');
  
  // Escape special characters in iCal format
  const escapeICS = (text: string) => {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };
  
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Śmieciarka Jedzie//PL
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${Date.now()}-${Math.random().toString(36).substr(2, 9)}@smieciarkajedzie.com
DTSTAMP:${dtStamp}
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${escapeICS(event.title)}
DESCRIPTION:${escapeICS(event.description)}
LOCATION:${escapeICS(event.location || '')}
STATUS:CONFIRMED
SEQUENCE:0
END:VEVENT
END:VCALENDAR`;
  
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'odbior-odpadow.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const Item = ({ item, borderColor, today }: any) => {
  const daysLeft = daysLeftTo(item.date, today);
  const theme = useTheme() as AppTheme;
  const typesOfGarbage = item.types.sort((a: string, b: string) =>
    a.localeCompare(b),
  );
  const event = generateEvent(item, typesOfGarbage);
  const isUrgent = daysLeft <= 1;
  const isToday = daysLeft === 0;
  
  // Handle calendar button click for iOS
  const handleCalendarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    downloadICSFile(event);
  };

  return (
    <Box
      as={motion.div}
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      position="relative"
      backgroundColor={theme.backgroundCard}
      borderWidth="1px"
      borderColor={isUrgent ? `${theme.red}50` : theme.borderCard}
      borderRadius={THEME.borderRadius}
      boxShadow={isUrgent ? `0 4px 20px ${theme.red}15` : theme.shadowSm}
      mt="3"
      mb="3"
      overflow="hidden"
      transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{ 
        boxShadow: isUrgent ? `0 8px 30px ${theme.red}20` : theme.shadowLg,
        borderColor: isUrgent ? `${theme.red}70` : theme.primary + "40"
      }}
    >
      {/* Urgent indicator */}
      {isUrgent && (
        <UrgentPulse 
          $theme={theme}
          animate={{ 
            opacity: [0.5, 1, 0.5],
            scale: [1, 1.05, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      
      {/* Date and garbage icons section */}
      <Flex
        p="4"
        position="relative"
        zIndex="1"
        borderBottom="1px solid"
        borderColor={theme.border}
      >
        <Box flex="1">
          <Text 
            fontFamily="'Outfit', sans-serif"
            fontSize="lg" 
            fontWeight="700" 
            color={isUrgent ? theme.red : theme.text}
            letterSpacing="-0.02em"
          >
            {getDaysLeftDescription(daysLeft)}
          </Text>
          <Text 
            fontSize="sm" 
            color={theme.textSecondary}
            mt="1"
            fontWeight="500"
          >
            {dayjs(item.date).format("D MMMM, dddd")}
          </Text>
          
          {isToday && (
            <Badge 
              mt="2" 
              colorScheme="red" 
              borderRadius="full" 
              px="3"
              py="1"
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              🔔 Dziś!
            </Badge>
          )}
          {isUrgent && !isToday && (
            <Badge 
              mt="2" 
              colorScheme="orange" 
              borderRadius="full" 
              px="3"
              py="1"
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              ⏰ Jutro
            </Badge>
          )}
        </Box>
        
        <GarbageIconContainer>
          {renderGarbageIcons(typesOfGarbage)}
        </GarbageIconContainer>
      </Flex>
      
      {/* Garbage types section */}
      <Box p="4" pt="3" pb="3">
        <Wrap spacing="1">
          {renderGarbageLabels(typesOfGarbage)}
        </Wrap>
      </Box>
      
      {/* Calendar button */}
      <Flex 
        p="3" 
        justifyContent="flex-end"
        borderTop="1px solid"
        borderColor={theme.border}
        bg={theme.backgroundMuted}
      >
        {isIOS() ? (
          <AddToCalendarButton 
            $theme={theme}
            onClick={handleCalendarClick}
          >
            <CalendarPlus size={14} />
            Dodaj do kalendarza
          </AddToCalendarButton>
        ) : (
          /* @ts-ignore */
          <ICalendarLink event={event}>
            <AddToCalendarButton $theme={theme}>
              <CalendarPlus size={14} />
              Dodaj do kalendarza
            </AddToCalendarButton>
          </ICalendarLink>
        )}
      </Flex>
    </Box>
  );
};

const GarbageIconContainer = styled.div`
  display: flex;
  gap: 0.35rem;
  justify-content: flex-end;
  align-items: center;
`;
